<template>
  <div class="stock-adjustment-container stock-adjustment-page--glass glass-tab-page">
    <BrucdcumHeader
      :breadcrumb="['Inventory', 'Stock Adjustments']"
      :show-back-button="true"
      :back-fallback="backFallbackRoute"
    />

    <div class="glass-tab-view glass-tab-view--flush sa-glass-shell">
      <div class="glass-tab-view__content sa-page-stack">
        <div class="sa-header sa-glass-panel glass-tab-panel--nested">
          <div class="sa-header-title">
            <div class="sa-title-row">
              <h2 class="sa-title-heading">
                <span v-if="companyName" class="sa-title-company">{{ companyName }}</span>
                <span v-if="companyName" class="sa-title-sep" aria-hidden="true">·</span>
                <span>Stock Adjustments</span>
              </h2>
              <div class="sa-title-actions">
                <SecondaryButton
                  label="Export to CSV"
                  icon="bi bi-download"
                  custom-class="sa-action-btn"
                  :disabled="!filteredAdjustments.length"
                  @click="exportToCSV"
                />
                <PrimaryButton
                  label="Add New Stock Adjustment"
                  icon="bi bi-plus-lg"
                  @click="goToAddNewStockAdjustment"
                />
              </div>
            </div>
            <div class="sa-report-info">
              <span>Warehouse: {{ warehouseFilterLabel }}</span>
              <span v-if="fromDate && toDate">Date Range: {{ dateFilterLabel }} ({{ fromDate }} to {{ toDate }})</span>
              <span v-else>Date Range: {{ dateFilterLabel }}</span>
              <span>{{ filteredAdjustments.length }} record{{ filteredAdjustments.length === 1 ? '' : 's' }}</span>
            </div>
          </div>

          <div class="sa-header-filters">
            <div class="sa-filter-field sa-filter-field--warehouse">
              <LabelDropdown
                label="Warehouse"
                select-id="sa-warehouse"
                v-model="warehouseFilter"
                inline
                flex
                searchable
                :options="warehouseOptions"
              />
            </div>
            <div class="sa-filter-field sa-filter-field--range">
              <LabelDropdown
                label="Date Range"
                select-id="sa-date-range"
                v-model="dateFilter"
                inline
                flex
                :searchable="false"
                :options="dateFilterOptions"
                @change="onDateRangeChange"
              />
            </div>
            <div class="sa-filter-field sa-filter-field--date">
              <LabelDateField
                input-id="sa-from-date"
                label="From"
                v-model="fromDate"
                placeholder="MM/DD/YYYY"
                flex
                :readonly="dateFilter !== 'custom'"
              />
            </div>
            <div class="sa-filter-field sa-filter-field--date">
              <LabelDateField
                input-id="sa-to-date"
                label="To"
                v-model="toDate"
                placeholder="MM/DD/YYYY"
                flex
                :readonly="dateFilter !== 'custom'"
              />
            </div>
            <div class="sa-filter-update reports-filter-update">
              <span class="reports-filter-update__spacer" aria-hidden="true"></span>
              <div class="reports-filter-update__action">
                <PrimaryButton label="Update" icon="bi bi-arrow-repeat" compact @click="applyFilters" />
              </div>
            </div>
          </div>
        </div>

        <div class="reports-account-glass-panel sa-table-panel glass-tab-panel--nested">
          <div v-if="loading" class="sa-loading-message">Loading stock adjustments...</div>
          <div v-if="errorMessage" class="sa-error-message">{{ errorMessage }}</div>

          <div v-if="!loading && !filteredAdjustments.length" class="consultant-glass-table-no-records">
            No records to show
          </div>
          <div v-else-if="!loading" class="consultant-glass-table-section reports-account-glass-section">
            <div class="reports-account-glass-header">
              <div class="reports-account-glass-header-title">
                <i class="bi bi-sliders reports-account-glass-header-icon" aria-hidden="true"></i>
                Stock Adjustments
              </div>
            </div>
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table reports-account-glass-table sa-glass-table">
                  <thead>
                    <tr>
                      <th class="sa-col-date">Date</th>
                      <th class="sa-col-warehouse">Warehouse</th>
                      <th class="sa-col-item">Item Name</th>
                      <th class="sa-col-comment">Comment</th>
                      <th class="sa-col-qty">Current Qty</th>
                      <th class="sa-col-qty">Adjusted Qty</th>
                      <th class="sa-col-amount">Current Value</th>
                      <th class="sa-col-amount">Rate</th>
                      <th class="sa-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(adj, idx) in filteredAdjustments"
                      :key="adj.uniqueKey"
                      class="sa-adjustment-row consultant-glass-table-row reports-account-transaction-row"
                      :style="{ '--sa-row-delay': `${Math.min(idx * 0.03, 0.6)}s` }"
                    >
                      <td class="sa-col-date">{{ formatDate(adj.createdAt) }}</td>
                      <td class="sa-col-warehouse"><span class="sa-cell-ellipsis" :title="adj.warehouseName">{{ adj.warehouseName }}</span></td>
                      <td class="sa-col-item"><span class="sa-cell-ellipsis" :title="adj.itemName">{{ adj.itemName }}</span></td>
                      <td class="sa-col-comment"><span class="sa-cell-ellipsis" :title="adj.comment || '-'">{{ adj.comment || '-' }}</span></td>
                      <td class="sa-col-qty">{{ adj.currentQty ?? '-' }}</td>
                      <td class="sa-col-qty" :class="adj.adjustedQty > 0 ? 'sa-qty-pos' : adj.adjustedQty < 0 ? 'sa-qty-neg' : ''">{{ adj.adjustedQty }}</td>
                      <td class="sa-col-amount">{{ formatCurrency(adj.currentValue) }}</td>
                      <td class="sa-col-amount">{{ formatCurrency(adj.rate) }}</td>
                      <td class="sa-col-actions">
                        <div class="sa-action-buttons">
                          <SecondaryButton label="Edit" icon="bi bi-pencil" compact @click="editAdjustment(adj)" />
                          <SecondaryButton label="Delete" icon="bi bi-trash" compact custom-class="sa-delete-btn" @click="showDeleteModalHandler(adj.id)" />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Delete Stock Adjustment"
      message="Are you sure you want to delete this stock adjustment?"
      :details="deleteModalDetails"
      :loading="deleteLoading"
      @confirm="confirmDelete"
      @cancel="closeDeleteModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      @close="closeSuccessModal"
    />
  </div>
</template>

<script>
import '../styles/StockAdjustment.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import logger from '@/utils/logger';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ApiService from '@/services/api.service';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

const DATE_FILTER_OPTIONS = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_week', label: 'Last week' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_fy', label: 'This financial year' },
  { value: 'last_fy', label: 'Last financial year' },
  { value: 'this_fq', label: 'This financial quarter' },
  { value: 'last_fq', label: 'Last financial quarter' },
  { value: 'fytd', label: 'Financial year to date' },
  { value: 'custom', label: 'Custom' },
]);

export default {
  name: 'StockAdjustment',
  components: {
    BrucdcumHeader,
    DeleteConfirmationModal,
    SuccessModal,
    LabelDropdown,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      dateFilter: 'all',
      fromDate: '',
      toDate: '',
      warehouseFilter: 'all',
      adjustments: [],
      warehouses: [],
      deleteId: null,
      deletingAdjustment: null,
      showDeleteModal: false,
      deleteLoading: false,
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      loading: false,
      errorMessage: '',
      dateFilterOptions: DATE_FILTER_OPTIONS,
    };
  },
  computed: {
    backFallbackRoute() {
      const companyId = this.$route.params.id;
      return companyId ? `/company/${companyId}/dashboard/inventory/center` : '';
    },
    companyName() {
      const storeCompany = this.$store.state.companies?.currentCompany;
      if (storeCompany?.name) return storeCompany.name;
      return this.$store.state.currentCompany?.name || '';
    },
    uniqueWarehouses() {
      return this.warehouses
        .filter((warehouse) => warehouse.isActive)
        .map((warehouse) => warehouse.name)
        .sort();
    },
    warehouseOptions() {
      return [
        { value: 'all', label: 'All Warehouses' },
        ...this.uniqueWarehouses.map((name) => ({ value: name, label: name })),
      ];
    },
    warehouseFilterLabel() {
      return this.warehouseOptions.find((o) => o.value === this.warehouseFilter)?.label || this.warehouseFilter;
    },
    dateFilterLabel() {
      return this.dateFilterOptions.find((o) => o.value === this.dateFilter)?.label || this.dateFilter;
    },
    filteredAdjustments() {
      let filtered = this.adjustments;

      if (this.warehouseFilter && this.warehouseFilter !== 'all') {
        filtered = filtered.filter((adj) => adj.warehouseName === this.warehouseFilter);
      }

      if (this.fromDate || this.toDate) {
        let from = null;
        let to = null;

        if (this.fromDate) {
          const fromParts = this.fromDate.split('/');
          if (fromParts.length === 3) {
            const [month, day, year] = fromParts;
            from = new Date(year, month - 1, day, 0, 0, 0);
          }
        }

        if (this.toDate) {
          const toParts = this.toDate.split('/');
          if (toParts.length === 3) {
            const [month, day, year] = toParts;
            to = new Date(year, month - 1, day, 23, 59, 59);
          }
        }

        filtered = filtered.filter((adj) => {
          if (!adj.createdAt) return false;
          const adjDate = new Date(adj.createdAt);
          if (from && adjDate < from) return false;
          if (to && adjDate > to) return false;
          return true;
        });
      }

      return filtered;
    },
    deleteModalDetails() {
      if (!this.deletingAdjustment) return {};
      return {
        itemName: this.deletingAdjustment.itemName,
        warehouseName: this.deletingAdjustment.warehouseName,
        adjustedQty: this.deletingAdjustment.adjustedQty,
        date: this.formatDate(this.deletingAdjustment.createdAt),
      };
    },
  },
  mounted() {
    this.fetchAdjustments();
    this.fetchWarehouses();
  },
  methods: {
    onDateRangeChange() {
      if (this.dateFilter === 'custom') return;
      this.setDateRangeForFilter(this.dateFilter);
    },
    setDateRangeForFilter(filter) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      if (filter === 'this_month') {
        this.fromDate = `${mm}/01/${yyyy}`;
        this.toDate = `${mm}/${dd}/${yyyy}`;
      } else if (filter === 'last_month') {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const lyyyy = lastMonth.getFullYear();
        const lmm = String(lastMonth.getMonth() + 1).padStart(2, '0');
        const ldd = String(lastMonthEnd.getDate()).padStart(2, '0');
        this.fromDate = `${lmm}/01/${lyyyy}`;
        this.toDate = `${lmm}/${ldd}/${lyyyy}`;
      } else if (filter === 'this_week') {
        const day = today.getDay();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - day);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        this.fromDate = `${String(weekStart.getMonth() + 1).padStart(2, '0')}/${String(weekStart.getDate()).padStart(2, '0')}/${weekStart.getFullYear()}`;
        this.toDate = `${String(weekEnd.getMonth() + 1).padStart(2, '0')}/${String(weekEnd.getDate()).padStart(2, '0')}/${weekEnd.getFullYear()}`;
      } else if (filter === 'last_week') {
        const day = today.getDay();
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - day - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        this.fromDate = `${String(lastWeekStart.getMonth() + 1).padStart(2, '0')}/${String(lastWeekStart.getDate()).padStart(2, '0')}/${lastWeekStart.getFullYear()}`;
        this.toDate = `${String(lastWeekEnd.getMonth() + 1).padStart(2, '0')}/${String(lastWeekEnd.getDate()).padStart(2, '0')}/${lastWeekEnd.getFullYear()}`;
      } else if (filter === 'this_fy') {
        const fyStart = new Date(today.getFullYear(), 3, 1);
        if (today.getMonth() < 3) fyStart.setFullYear(today.getFullYear() - 1);
        this.fromDate = `${String(fyStart.getMonth() + 1).padStart(2, '0')}/${String(fyStart.getDate()).padStart(2, '0')}/${fyStart.getFullYear()}`;
        this.toDate = `${mm}/${dd}/${yyyy}`;
      } else if (filter === 'last_fy') {
        const fyStart = new Date(today.getFullYear(), 3, 1);
        if (today.getMonth() < 3) fyStart.setFullYear(today.getFullYear() - 2);
        else fyStart.setFullYear(today.getFullYear() - 1);
        const fyEnd = new Date(fyStart.getFullYear() + 1, 2, 31);
        this.fromDate = `${String(fyStart.getMonth() + 1).padStart(2, '0')}/${String(fyStart.getDate()).padStart(2, '0')}/${fyStart.getFullYear()}`;
        this.toDate = `${String(fyEnd.getMonth() + 1).padStart(2, '0')}/${String(fyEnd.getDate()).padStart(2, '0')}/${fyEnd.getFullYear()}`;
      } else if (filter === 'this_fq') {
        const q = Math.floor(today.getMonth() / 3);
        const fqStart = new Date(today.getFullYear(), q * 3, 1);
        const fqEnd = new Date(today.getFullYear(), (q + 1) * 3, 0);
        this.fromDate = `${String(fqStart.getMonth() + 1).padStart(2, '0')}/${String(fqStart.getDate()).padStart(2, '0')}/${fqStart.getFullYear()}`;
        this.toDate = `${String(fqEnd.getMonth() + 1).padStart(2, '0')}/${String(fqEnd.getDate()).padStart(2, '0')}/${fqEnd.getFullYear()}`;
      } else if (filter === 'last_fq') {
        let q = Math.floor(today.getMonth() / 3) - 1;
        let fqYear = today.getFullYear();
        if (q < 0) { q = 3; fqYear -= 1; }
        const fqStart = new Date(fqYear, q * 3, 1);
        const fqEnd = new Date(fqYear, (q + 1) * 3, 0);
        this.fromDate = `${String(fqStart.getMonth() + 1).padStart(2, '0')}/${String(fqStart.getDate()).padStart(2, '0')}/${fqStart.getFullYear()}`;
        this.toDate = `${String(fqEnd.getMonth() + 1).padStart(2, '0')}/${String(fqEnd.getDate()).padStart(2, '0')}/${fqEnd.getFullYear()}`;
      } else if (filter === 'fytd') {
        const fyStart = new Date(today.getFullYear(), 3, 1);
        if (today.getMonth() < 3) fyStart.setFullYear(today.getFullYear() - 1);
        this.fromDate = `${String(fyStart.getMonth() + 1).padStart(2, '0')}/${String(fyStart.getDate()).padStart(2, '0')}/${fyStart.getFullYear()}`;
        this.toDate = `${mm}/${dd}/${yyyy}`;
      } else if (filter === 'all') {
        this.fromDate = '';
        this.toDate = '';
      }
    },
    async fetchAdjustments() {
      this.errorMessage = '';
      this.loading = true;
      const companyId = this.$route.params.id;
      if (!companyId) {
        this.errorMessage = 'No company selected.';
        this.loading = false;
        return;
      }
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/stock-adjustments`);
        if (!response.ok) throw new Error('Failed to fetch stock adjustments');
        const data = await response.json();
        this.adjustments = data.flatMap((adj) =>
          adj.items.map((item, itemIndex) => ({
            id: adj.id,
            uniqueKey: `${adj.id}-${item.id || itemIndex}`,
            warehouseName: this.getWarehouseName(adj.warehouse, adj.warehouseId),
            warehouseId: adj.warehouse?.id || adj.warehouseId || null,
            itemName: item.inventoryItem ? item.inventoryItem.name : 'Unknown Item',
            itemId: item.inventoryItemId,
            comment: item.comment,
            currentQty: item.currentQty,
            adjustedQty: item.adjustedQty,
            currentValue: item.currentValue,
            rate: item.rate,
            createdAt: adj.createdAt,
            updatedAt: adj.updatedAt,
            adjustmentAccountId: adj.adjustmentAccount,
            companyId: adj.companyId,
          }))
        );
      } catch (error) {
        this.errorMessage = error.message || 'Failed to fetch stock adjustments';
      } finally {
        this.loading = false;
      }
    },
    async fetchWarehouses() {
      try {
        const companyId = this.$route.params.id;
        if (!companyId) return;
        const response = await ApiService.get(`/warehouses/company/${companyId}`);
        if (response.data?.success && response.data.data?.warehouses) {
          this.warehouses = response.data.data.warehouses;
        } else {
          this.warehouses = [];
        }
      } catch (error) {
        logger.error('Error fetching warehouses:', error);
        this.warehouses = [];
      }
    },
    goToAddNewStockAdjustment() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/stock-adjustment/new`);
    },
    exportToCSV() {
      try {
        const headers = ['Date', 'Warehouse', 'Item Name', 'Comment', 'Current Qty', 'Adjusted Qty', 'Current Value', 'Rate'];
        const csvContent = [
          headers.join(','),
          ...this.filteredAdjustments.map((adj) => [
            this.formatDate(adj.createdAt),
            `"${adj.warehouseName}"`,
            `"${adj.itemName}"`,
            `"${adj.comment || '-'}"`,
            adj.currentQty || '-',
            adj.adjustedQty,
            adj.currentValue || '-',
            adj.rate || '-',
          ].join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `stock-adjustments-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.successTitle = 'Export Successful';
        this.successMessage = 'CSV file has been downloaded successfully.';
        this.showSuccessModal = true;
      } catch (error) {
        logger.error('Error exporting to CSV:', error);
        this.errorMessage = 'Failed to export CSV file';
        setTimeout(() => { this.errorMessage = ''; }, 3000);
      }
    },
    showDeleteModalHandler(id) {
      this.deleteId = id;
      this.deletingAdjustment = this.adjustments.find((adj) => adj.id === id);
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteId = null;
      this.deletingAdjustment = null;
      this.deleteLoading = false;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successTitle = 'Success';
      this.successMessage = '';
    },
    async confirmDelete() {
      if (!this.deleteId) return;
      this.deleteLoading = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/stock-adjustments/${this.deleteId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Failed to delete stock adjustment');
        this.adjustments = this.adjustments.filter((adj) => adj.id !== this.deleteId);
        this.closeDeleteModal();
        this.successTitle = 'Deleted Successfully';
        this.successMessage = 'The stock adjustment has been deleted successfully.';
        this.showSuccessModal = true;
      } catch (error) {
        logger.error('Error deleting stock adjustment:', error);
        this.errorMessage = error.message || 'Failed to delete stock adjustment';
        this.closeDeleteModal();
        setTimeout(() => { this.errorMessage = ''; }, 5000);
      }
    },
    applyFilters() {
      const totalRecords = this.filteredAdjustments.length;
      if (totalRecords === 0) {
        this.errorMessage = 'No records found matching the selected filters.';
        setTimeout(() => { this.errorMessage = ''; }, 3000);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (error) {
        return '-';
      }
    },
    formatCurrency(value) {
      if (!value || isNaN(value)) return '-';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(value);
    },
    editAdjustment(adjustment) {
      this.$router.push({
        path: `/company/${this.$route.params.id}/dashboard/inventory/stock-adjustment/new`,
        query: {
          editId: adjustment.id,
          warehouseId: adjustment.warehouseId,
          itemId: adjustment.itemId,
        },
      });
    },
    getWarehouseName(warehouseObj, warehouseId) {
      if (warehouseObj?.name) return warehouseObj.name;
      if (warehouseId && this.warehouses.length > 0) {
        const warehouse = this.warehouses.find((w) => w.id === warehouseId);
        if (warehouse) return warehouse.name;
      }
      if (typeof warehouseObj === 'string') return warehouseObj;
      return 'Unknown Warehouse';
    },
  },
};
</script>
